import { ApolloError, useMutation } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ADD_CASH_IN_MUTATION, ADD_CASH_OUT_MUTATION } from './cash-shifts.gql';

import { centsToMajorString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface CashShift {
  id: string;
}

interface CashShiftMovementDialogProps {
  shift: CashShift;
  kind: 'cash_in' | 'cash_out';
  on_close: () => void;
  on_submitted: () => void;
}

export function CashShiftMovementDialog({
  shift,
  kind,
  on_close,
  on_submitted,
}: CashShiftMovementDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [amount_major, setAmountMajor] = useState('0.00');
  const [reason, setReason] = useState('');
  const [error_text, setErrorText] = useState<string | null>(null);

  const amount_cents = useMemo(() => {
    try {
      return majorStringToCents(amount_major || '0').toString();
    } catch {
      return '';
    }
  }, [amount_major]);

  const [addCashIn, { loading: in_loading }] = useMutation(ADD_CASH_IN_MUTATION);
  const [addCashOut, { loading: out_loading }] = useMutation(ADD_CASH_OUT_MUTATION);
  const submitting = in_loading || out_loading;

  const can_submit =
    amount_cents.length > 0 &&
    BigInt(amount_cents || '0') > 0n &&
    reason.trim().length > 0 &&
    !submitting;

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    setErrorText(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const variables = {
      input: {
        cash_shift_id: shift.id,
        amount_cents,
        reason: reason.trim(),
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
    };
    try {
      const op = kind === 'cash_in' ? addCashIn : addCashOut;
      await op({
        variables,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      on_submitted();
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code.startsWith('cash_out_exceeds_cap')) {
          const cap = code.split(':')[1] ?? '50000';
          setErrorText(t('cash_shifts.error_cash_out_cap', { cap }));
          return;
        }
        if (code === 'amount_must_be_positive') {
          setErrorText(t('cash_shifts.error_amount_positive'));
          return;
        }
        if (code === 'reason_required') {
          setErrorText(t('cash_shifts.error_reason_required'));
          return;
        }
        if (code === 'cash_shift_not_owned_by_actor') {
          setErrorText(t('cash_shifts.error_not_owner'));
          return;
        }
        if (code.startsWith('cash_shift_not_open')) {
          setErrorText(t('cash_shifts.error_not_open'));
          return;
        }
        setErrorText(code);
        return;
      }
      if (err instanceof Error) setErrorText(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {kind === 'cash_in'
              ? t('cash_shifts.cash_in_dialog_title')
              : t('cash_shifts.cash_out_dialog_title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="mv-amount">{t('cash_shifts.amount')}</Label>
            <Input
              id="mv-amount"
              inputMode="decimal"
              className="font-mono"
              value={amount_major}
              onChange={(e) => setAmountMajor(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              {t('cash_shifts.amount_hint', {
                value: centsToMajorString(amount_cents || '0'),
              })}
            </p>
            {kind === 'cash_out' ? (
              <p className="text-xs text-muted-foreground">
                {t('cash_shifts.cash_out_cap_hint')}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="mv-reason">{t('cash_shifts.reason')}</Label>
            <textarea
              id="mv-reason"
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
            />
          </div>
          {error_text ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {error_text}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {submitting ? t('common.loading') : t('cash_shifts.record')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
